/**
 * apiHealth — « votre connexion va bien, c'est Médard qui ne répond pas ».
 *
 * Le ruban réseau existant ne lit que `navigator.onLine`. Il couvre le cas où
 * le TÉLÉPHONE perd la ligne, et il le dit très bien. Il ne couvre pas l'autre
 * cas, celui d'août : le téléphone va parfaitement, et c'est le SERVEUR qui ne
 * répond plus. `navigator.onLine` vaut alors `true`, aucun ruban ne s'affiche,
 * et l'invité récolte une dizaine d'états d'échec de section sans explication.
 *
 * Deux pannes, deux phrases. Dire « vous êtes hors ligne » à quelqu'un dont la
 * connexion est intacte, ce serait affirmer quelque chose de faux (Loi 1) —
 * et l'envoyer chercher le problème là où il n'est pas.
 *
 * Ce que l'on compte comme « injoignable », et ce qu'on refuse de compter :
 *   • une panne de transport (aucune réponse : DNS, refus, timeout)  → OUI
 *   • un 5xx (le serveur répond, mais il est cassé)                  → OUI
 *   • un 4xx → NON : le serveur est vivant et vient de nous répondre
 *     « non ». Un 404 sur un monument absent n'est pas une panne.
 *   • une requête annulée → NON : c'est nous qui avons raccroché.
 *   • `navigator.onLine === false` → NON : c'est l'appareil, et le ruban
 *     hors-ligne dit déjà la vérité. On ne va pas accuser le serveur.
 *
 * Il faut DEUX échecs consécutifs qualifiants pour basculer : une requête
 * malheureuse sur un wifi d'hôtel ne doit pas déclencher un ruban. Le moindre
 * succès rétablit l'état immédiatement.
 */

// Exporté le 25/10/2026 : la balade qui garde ses fiches s'arrête après le
// même nombre d'échecs consécutifs que celui qui lève le ruban — deux portes
// qui jugeraient la panne à deux seuils différents se contrediraient.
export const FAILURES_BEFORE_ALARM = 2;
const PROBE_INTERVAL_MS = 15000;

let consecutiveFailures = 0;
let unreachable = false;
let probeTimer = null;
const listeners = new Set();

export const isOnline = () => typeof navigator === 'undefined' || navigator.onLine !== false;

function emit() {
    listeners.forEach((fn) => {
        try { fn(unreachable); } catch { /* un abonné qui jette ne doit pas
            emporter les autres — ce module ne casse jamais l'app */ }
    });
}

function setUnreachable(next) {
    if (next === unreachable) return;
    unreachable = next;
    if (unreachable) startProbe(); else stopProbe();
    emit();
}

const isCancelled = (err) =>
    err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.name === 'AbortError';

/**
 * Le code HTTP porté par une erreur, quelle que soit sa forme.
 *
 * Axios range la réponse sous `err.response.status`. Les chemins de la VOIX
 * n'utilisent pas axios : ils lèvent une `Error` nue qui porte `.status`
 * directement (« ask/stream failed: 429 »). Ne lire que la forme axios
 * ferait passer un 429 pour une absence totale de réponse — c'est-à-dire
 * prendre « doucement, vous allez trop vite » pour une panne de serveur.
 */
const statusOf = (err) => err?.response?.status ?? err?.status;

/** Un échec compte-t-il comme « le serveur ne répond pas » ? */
export function countsAsUnreachable(err) {
    if (!err) return false;
    if (isCancelled(err)) return false;         // c'est nous qui avons raccroché
    if (!isOnline()) return false;              // l'appareil : autre ruban, autre phrase
    const status = statusOf(err);
    if (status === undefined) return true;      // aucune réponse du tout
    return status >= 500;                        // le serveur répond, cassé
}

export function reportApiFailure(err) {
    if (!err || isCancelled(err) || !isOnline()) return;
    const status = statusOf(err);
    // UN 4xx EST UNE PREUVE DE VIE. Le serveur vient de répondre « non » : il
    // est debout, et le compteur doit repartir de zéro. Se contenter d'ignorer
    // le 4xx laissait la séquence panne → 404 → panne déclencher l'alarme,
    // alors que le 404 prouvait entre-temps que tout allait bien.
    if (status !== undefined && status < 500) { reportApiSuccess(); return; }
    consecutiveFailures += 1;
    if (consecutiveFailures >= FAILURES_BEFORE_ALARM) setUnreachable(true);
}

/**
 * Signale l'issue d'un appel `fetch` — les chemins de la voix, qui ne passent
 * pas par l'intercepteur axios. Or parler à Médard EST l'usage principal : un
 * invité qui arrive sur une coquille servie par le service worker et ne fait
 * que poser des questions ne déclenchait aucune requête axios, donc aucun
 * ruban, alors que rien ne lui répondait.
 *
 * Toute réponse HTTP, même mauvaise, prouve que le serveur est là.
 */
export function reportFetchResponse(res) {
    const status = res?.status;
    if (status === undefined) return;
    if (status >= 500) reportApiFailure({ status });
    else reportApiSuccess();
}

export function reportApiSuccess() {
    consecutiveFailures = 0;
    setUnreachable(false);
}

export function isApiUnreachable() {
    return unreachable;
}

/**
 * Sonde de rétablissement. Tant que le serveur est déclaré injoignable, rien ne
 * garantit qu'une nouvelle requête partira : un invité qui ne touche plus à
 * rien resterait devant un ruban éternel. On interroge donc `GET /api/` — la
 * route racine, sans base de données ni fournisseur, la moins chère qui existe.
 * En `fetch` nu, pour ne pas repasser par l'intercepteur qui nous a appelés.
 */
function startProbe() {
    if (probeTimer || typeof window === 'undefined') return;
    probeTimer = setInterval(async () => {
        if (!isOnline()) return;                 // inutile de sonder sans ligne
        try {
            const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
            const res = await fetch(`${base}/api/`, { method: 'GET', cache: 'no-store' });
            if (res.ok) reportApiSuccess();
        } catch { /* toujours injoignable — on retentera */ }
    }, PROBE_INTERVAL_MS);
}

function stopProbe() {
    if (probeTimer) { clearInterval(probeTimer); probeTimer = null; }
}

export function subscribeApiHealth(fn) {
    listeners.add(fn);
    return () => {
        listeners.delete(fn);
        if (listeners.size === 0) stopProbe();
    };
}

/** Remise à zéro — pour les tests, et pour eux seuls. */
export function __resetApiHealth() {
    consecutiveFailures = 0;
    unreachable = false;
    stopProbe();
    listeners.clear();
}
